/**
 * NOT-590 — pricebook category tree.
 *
 * Categories are folders for browsing the catalog. They carry no money and are
 * never a reporting dimension: revenue questions are answered by business unit,
 * job type and item type, not by which folder an item happens to sit in.
 *
 * ONE tree per item family: "Black" under materials and "Black" under equipment
 * are different folders, so a caller filing an item asks for its own family.
 */
import { useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiDelete, apiGet, apiPost, apiPut } from '@/lib/api';
import { pricebookKeys } from '@/lib/queryKeys';
import { applyPositions } from '@/components/organisms/pricebook/categories/category-tree-drag';
import { useApiMutation } from '../useApiMutation';
import { usePricebookApi } from './usePricebookApi';
import type {
  BulkEditCategoryItemsDto,
  CategoryItemRow,
  CategoryPosition,
  CreateCategoryDto,
  ItemCategoryMembership,
  MoveCategoryDto,
  PricebookCategoryNode,
  PricebookCategoryRow,
  UpdateCategoryDto,
  UpdateCategoryScopeDto,
} from '@/types/pricebook';

/** Which family's tree to read. Services and bundles share SERVICE. */
export type CategoryFamily = 'SERVICE' | 'MATERIAL' | 'EQUIPMENT';

interface TreeParams {
  /** Limit to one family's folders; omitted reads every family. */
  itemFamily?: CategoryFamily;
  /**
   * Management view — owner/admin see folders their own BU membership would
   * otherwise hide, because you cannot administer what you cannot see. The
   * server ignores this for callers without the scope permission.
   */
  manage?: boolean;
  enabled?: boolean;
}

export function useCategoryTree({ manage, itemFamily, enabled = true }: TreeParams = {}) {
  const { client, ep } = usePricebookApi();

  return useQuery({
    queryKey: pricebookKeys.categoryTree({ manage }).concat([itemFamily ?? 'all']),
    queryFn: () => {
      const params = new URLSearchParams();
      if (manage) params.set('manage', 'true');
      if (itemFamily) params.set('itemFamily', itemFamily);
      const qs = params.toString();
      return apiGet<PricebookCategoryNode[]>(client, `${ep.categoryTree()}${qs ? `?${qs}` : ''}`);
    },
    enabled,
  });
}

/** Flat, depth-annotated list — handy for pickers and select menus. */
export function useCategoryList({ itemFamily, enabled = true }: TreeParams = {}) {
  const { client, ep } = usePricebookApi();

  return useQuery({
    queryKey: pricebookKeys.categories().concat(['list', itemFamily ?? 'all']),
    queryFn: () =>
      apiGet<PricebookCategoryRow[]>(
        client,
        `${ep.categoryList()}${itemFamily ? `?itemFamily=${itemFamily}` : ''}`,
      ),
    enabled,
  });
}

export function useCategoryItems(
  categoryId: string | undefined,
  options: { includeDescendants?: boolean; manage?: boolean; enabled?: boolean } = {},
) {
  const { client, ep } = usePricebookApi();
  const { enabled = true, ...requestOptions } = options;

  return useQuery({
    // `enabled` controls observation only; it is not part of the server data
    // identity and must not split one category into separate cache entries.
    queryKey: pricebookKeys.categoryItems(categoryId ?? '', requestOptions),
    queryFn: ({ signal }) => {
      const search = new URLSearchParams();
      if (requestOptions.includeDescendants) search.set('includeDescendants', 'true');
      // Matches the management tree: an owner/admin administering a folder
      // hidden from their own BU must still see its contents.
      if (requestOptions.manage) search.set('manage', 'true');
      const qs = search.toString();
      return apiGet<CategoryItemRow[]>(
        client,
        `${ep.categoryItems(categoryId!)}${qs ? `?${qs}` : ''}`,
        { signal },
      );
    },
    enabled: Boolean(categoryId) && enabled,
  });
}

export function useItemCategories(lineItemId: string | undefined) {
  const { client, ep } = usePricebookApi();

  return useQuery({
    queryKey: pricebookKeys.itemCategories(lineItemId ?? ''),
    queryFn: () => apiGet<ItemCategoryMembership[]>(client, ep.itemCategories(lineItemId!)),
    enabled: Boolean(lineItemId),
  });
}

/** Invalidates every category-derived cache entry after a tree mutation. */
function useInvalidateCategories() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: pricebookKeys.categories() });
  };
}

export function useCreateCategory() {
  const { client, ep } = usePricebookApi();
  const invalidate = useInvalidateCategories();

  return useApiMutation<PricebookCategoryNode, CreateCategoryDto>({
    mutationFn: (dto) => apiPost(client, ep.categoryCreate(), dto),
    onSuccess: () => {
      invalidate();
      toast.success('Category created');
    },
  });
}

export function useUpdateCategory() {
  const { client, ep } = usePricebookApi();
  const invalidate = useInvalidateCategories();

  return useApiMutation<PricebookCategoryNode, { id: string; dto: UpdateCategoryDto }>({
    mutationFn: ({ id, dto }) => apiPut(client, ep.categoryUpdate(id), dto),
    onSuccess: () => {
      invalidate();
      toast.success('Category updated');
    },
  });
}

/**
 * Re-parent a folder and its whole subtree. Its own endpoint rather than a
 * field on update: the server revalidates the destination (cycle, depth, a
 * name already taken there) and a rename should never be able to move a folder.
 */
export function useMoveCategory() {
  const { client, ep } = usePricebookApi();
  const invalidate = useInvalidateCategories();

  return useApiMutation<PricebookCategoryNode, { id: string; dto: MoveCategoryDto }>({
    mutationFn: ({ id, dto }) => apiPut(client, ep.categoryMove(id), dto),
    onSuccess: () => {
      invalidate();
      toast.success('Folder moved');
    },
  });
}

/**
 * Owner/admin only, and audit-logged server-side. Changing who can BROWSE a
 * folder alters no invoice, no revenue attribution and no export.
 */
export function useUpdateCategoryScope() {
  const { client, ep } = usePricebookApi();
  const invalidate = useInvalidateCategories();

  return useApiMutation<PricebookCategoryNode, { id: string; dto: UpdateCategoryScopeDto }>({
    mutationFn: ({ id, dto }) => apiPut(client, ep.categoryScope(id), dto),
    onSuccess: () => {
      invalidate();
      toast.success('Visibility updated');
    },
  });
}

export function useDeleteCategory() {
  const { client, ep } = usePricebookApi();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateCategories();

  return useApiMutation<void, string>({
    // A category's item read can already be in flight when Delete is clicked.
    // Finish canceling it before the write so it cannot land after the soft
    // delete and surface an irrelevant 404 for an operation that succeeded.
    onMutate: (id) => queryClient.cancelQueries({ queryKey: pricebookKeys.categoryItems(id) }),
    mutationFn: (id) => apiDelete(client, ep.categoryDelete(id)),
    onSuccess: () => {
      invalidate();
      toast.success('Category deleted');
    },
  });
}

/** Persists a whole drag-and-drop rearrangement in one call. */
/**
 * Reorder folders, applying the move to the cached tree IMMEDIATELY.
 *
 * Without this the drop was persist-then-refetch: the row sprang back to where
 * it started, then jumped to its new home once the PUT and the refetch had both
 * returned. Two round-trips of visibly wrong state, which reads as the drag
 * glitching rather than as the network being slow.
 *
 * Every cached tree is patched, not just the one on screen — the same folders
 * are rendered by the settings page and by the item form's folder picker, and a
 * stale one would pop back into the old order the next time it was opened.
 */
export function useReorderCategories() {
  const { client, ep } = usePricebookApi();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateCategories();
  const treeKey = [...pricebookKeys.categories(), 'tree'];
  // Nothing stops a second drag while the first PUT is still in flight, and the
  // snapshots are then stacked: the older one predates the newer drag.
  const latestDrag = useRef(0);

  return useApiMutation<
    { updated: number },
    CategoryPosition[],
    {
      snapshot: Array<[readonly unknown[], PricebookCategoryNode[] | undefined]>;
      drag: number;
    }
  >({
    mutationFn: (positions) => apiPut(client, ep.categoryReorder(), { positions }),
    onMutate: async (positions) => {
      // An in-flight tree read would otherwise land after this and overwrite it.
      await queryClient.cancelQueries({ queryKey: treeKey });
      const snapshot = queryClient.getQueriesData<PricebookCategoryNode[]>({
        queryKey: treeKey,
      });
      queryClient.setQueriesData<PricebookCategoryNode[]>({ queryKey: treeKey }, (old) =>
        old ? applyPositions(old, positions) : old,
      );
      const drag = (latestDrag.current += 1);
      return { snapshot, drag };
    },
    onError: (_error, _positions, context) => {
      if (!context) return;
      // Only the newest drag may roll back. If another drop has been applied
      // since, this snapshot predates it, and restoring it would silently undo
      // a move the operator has already seen land. That newer drag's own
      // settle is what reconciles. The toast comes from useApiMutation.
      if (context.drag !== latestDrag.current) return;
      for (const [key, data] of context.snapshot) {
        queryClient.setQueryData(key, data);
      }
    },
    // Settled, not success: a failed reorder must also go back to server truth,
    // in case the rollback and the server disagree.
    //
    // Only the newest drag, for the same reason it alone may roll back. A
    // refetch answers with what the server has NOW, and an older drag settles
    // while the newer one's PUT is still in flight — so its invalidation would
    // pull back a tree that predates the newer drop and undo it on screen,
    // reinstating the spring-back this whole hook exists to remove. Skipping it
    // loses nothing: the newest drag always reaches its own settle, and it is
    // the only one whose refetch can show the order the operator ended on.
    onSettled: (_data, _error, _positions, context) => {
      if (context && context.drag !== latestDrag.current) return;
      invalidate();
    },
  });
}

export function useAddItemsToCategory() {
  const { client, ep } = usePricebookApi();
  const invalidate = useInvalidateCategories();

  return useApiMutation<
    { added: number },
    { categoryId: string; lineItemIds: string[]; makePrimary?: boolean }
  >({
    mutationFn: ({ categoryId, lineItemIds, makePrimary }) =>
      apiPost(client, ep.categoryItems(categoryId), { lineItemIds, makePrimary }),
    onSuccess: (result) => {
      invalidate();
      toast.success(result.added === 1 ? 'Item filed' : `${result.added} items filed`);
    },
  });
}

export function useRemoveItemsFromCategory() {
  const { client, ep } = usePricebookApi();
  const invalidate = useInvalidateCategories();

  return useApiMutation<{ removed: number }, { categoryId: string; lineItemIds: string[] }>({
    mutationFn: ({ categoryId, lineItemIds }) =>
      apiPost(client, ep.categoryItemsRemove(categoryId), { lineItemIds }),
    onSuccess: () => {
      invalidate();
      toast.success('Removed from category');
    },
  });
}

export function useReorderCategoryItems() {
  const { client, ep } = usePricebookApi();
  const invalidate = useInvalidateCategories();

  return useApiMutation<{ updated: number }, { categoryId: string; lineItemIds: string[] }>({
    mutationFn: ({ categoryId, lineItemIds }) =>
      apiPut(client, ep.categoryItemsOrder(categoryId), { lineItemIds }),
    onSuccess: invalidate,
  });
}

/**
 * "Select a folder, change a field on everything in it." The write lands on
 * each member ITEM — the category stores none of these fields, it is only a
 * selection shortcut.
 */
export function useBulkEditCategoryItems() {
  const queryClient = useQueryClient();
  const { client, ep } = usePricebookApi();

  return useApiMutation<
    { updated: number },
    { categoryId: string; dto: BulkEditCategoryItemsDto; includeDescendants?: boolean }
  >({
    mutationFn: ({ categoryId, dto, includeDescendants }) =>
      apiPost(
        client,
        `${ep.categoryBulkEdit(categoryId)}${includeDescendants ? '?includeDescendants=true' : ''}`,
        dto,
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: pricebookKeys.all() });
      toast.success(result.updated === 1 ? '1 item updated' : `${result.updated} items updated`);
    },
  });
}

export function useSetItemCategories() {
  const queryClient = useQueryClient();
  const { client, ep } = usePricebookApi();

  return useApiMutation<
    ItemCategoryMembership[],
    { lineItemId: string; categoryIds: string[]; primaryCategoryId?: string | null }
  >({
    mutationFn: ({ lineItemId, categoryIds, primaryCategoryId }) =>
      apiPut(client, ep.itemCategories(lineItemId), { categoryIds, primaryCategoryId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pricebookKeys.all() });
    },
  });
}
